import React, { useCallback, useEffect, useState } from 'react';
import { Grid, GridContainer } from '../../../shared/grid';
import { Formik, Form } from 'formik';
import { SelectChangeEvent } from '@mui/material/Select';
import { Autocomplete, ListItemText, TextField } from '@mui/material';
import { ValidCharacters, errorResponse, formatPhoneNumber, validateCharacters } from '../../../store/common';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import '../FieldSample.scss';
import * as Yup from 'yup';
import { GroupContact } from 'src/types';
import { getArea, getContactsCall, getGroupTable } from 'src/apis/getFuelSampleCalls';
import { useStoreContext } from 'src/store/context';
import { useAddAlert } from 'src/store/actions';
import { createUpdateGroup } from 'src/apis/mutationsFuelSample';
import { useHistory } from 'react-router-dom';
import { ROUTE_PATHS } from 'src/routes';
import { alertMessages } from 'src/constants';

/*
  Add custom Method to Yup
  Yup.array means it will apply to a array schema
  unique is the name of the custom method
  function has path and message where message is the error message to display if it fails, 
  path is the property to check for uniqueness
*/
Yup.addMethod<Yup.ArraySchema<any>>(Yup.array, 'unique', function(message, path){
  return this.test('unique', message, function(list){
    //it will test the schema 
    if(!list) return true; //no items to validate

    //in this case, if contactid is there 
    const mapper = (x:any)=> (path ? x[path]:x);

    const countOccurrences = (arr: any[])=>
      arr.reduce((acc,item)=>{
        const key = mapper(item);
        acc[key] = (acc[key] || 0)+1;
        return acc;
      }, {} as Record<string, number>);

    const occurrences = countOccurrences(list);

    const errors = list.map(item =>
      occurrences[mapper(item)] > 1 ? message :null  
    )

    if(errors.some(error => error!==null)){
      const errorArray:any[] = errors.map((error, index)=>
        error ? this.createError({path: `${this.path}[${index}].contactId`, message: error}):null
      ).filter(error=> error!==null)

      if(errorArray.length>0){
        throw new Yup.ValidationError(errorArray, null, this.path)
      }
    }
    return true
  })
})

export const validationSchema = Yup.object().shape(
    {
        groupName: Yup.string()
          .required('Group Name is Required'),
        area: Yup.object().nullable().shape({
          label: Yup.string().nullable()
            .required('Area Name is Required'),
        }).required('Area Name is Required'),
        contacts: Yup.array().of(
          Yup.object().shape({
            contactName: Yup.string().nullable()
              .required('Contact Name is Required'), 
            contactId: Yup.number().required()
          })
        ).unique(alertMessages.saveGroupContact, 'contactId').required('Must have Contacts'),

    }
);
type GroupProps ={
  setOpen: any,
  open: boolean,
  editGroup?: any,
  setCreateUpdateGroupSuccessful?:any
}
interface FormVals {
  groupId?: number|null,
  groupName: string,
  area: any,
  contacts: Array<GroupContact>,
  deleteContacts: Array<GroupContact>
}
interface CreateUpdateGroupParams {
  groupId: number|null,
  groupName: string|null,
  areaId: string|null,
  status: string|null,
  groupContacts?: Array<GroupContact>,
  deleteGroupContacts?: Array<GroupContact> |null
}

export const Group: React.FC<GroupProps> = ({setOpen, open, setCreateUpdateGroupSuccessful, editGroup}) =>{
	const [state, dispatch] = useStoreContext();
  const addAlert = useAddAlert(dispatch);
  const [token, setToken] = useState<string|null>(null);
  const [initVals, setInitVals] = useState<FormVals>({
    groupId: null,
    groupName: '', 
    area: null, 
    contacts:[{
      contactName: '',
      contactPhone: '',
      contactExtension: '',
      title: '',
      contactEmail: '',
      priority: 1,
      contactId: 0
    }], 
    deleteContacts: []
  })
  const [areas, setAreas] = useState<any[]>([]);
  const [areasCallComplete, setAreasCallComplete] = useState<boolean>(true);
  const [areaFailureCount, setAreaFailureCount] = useState(0);

  const [contacts, setContacts] = useState<any[]>([]);
  const [contactsCallComplete, setContactsCallComplete] = useState<boolean>(true);
  const [contactFailureCount, setContactFailureCount] = useState(0);

  const [, setGroupCallComplete] = useState<boolean>(true);
  const [, setGroupFailureCount] = useState(0);
  const [contactsCalled, setContactsCalled]= useState<boolean>(false);

  const history = useHistory();

  const handleSubmit= (values:any)=>{
    (async()=>{
      try{
        let groupContacts: GroupContact[] = [];
        let deleteGroupContacts: GroupContact[] = [];
        for(const contact of values.contacts) {
          const groupContact: GroupContact = {
            groupContactId: contact.groupContactId,
            contactId: contact.contactId,
            priority: contact.priority
          }
          groupContacts.push(groupContact);
        }

        const filteredContacts = values.deleteContacts?.filter(deleteContact => 
          !groupContacts?.some(contactToAdd => contactToAdd.contactId === deleteContact.contactId)
        );

        for(const deleteContact of filteredContacts) {
          const deleteGroupContact: GroupContact = {
            groupContactId: deleteContact.groupContactId,
          }
          deleteGroupContacts.push(deleteGroupContact)
        }

        let count =0;
        const contactsMoved = groupContacts?.map((contact)=>{
          const matchingObj = values.deleteContacts?.find(deleteContact => deleteContact.contactId === contact.contactId);
          if(matchingObj!==undefined){
            count=count+1;
          }
          return typeof matchingObj !=='undefined'? { ...contact, groupContactId: matchingObj?.groupContactId } : contact;
        });

        const params: CreateUpdateGroupParams = {
          groupId: values.groupId,
          groupName: values.groupName,
          areaId: values.area.id,
          status: null,
          groupContacts: count>0? contactsMoved:groupContacts,
          deleteGroupContacts: filteredContacts?.length<=0? null: deleteGroupContacts
        }
        const returnSample = await createUpdateGroup(token, addAlert, params);
        if(returnSample){
          setCreateUpdateGroupSuccessful(true);
          setOpen(false);
        }
      } catch(error:any){
        console.log('error', error);
        if(error?.message){
          errorResponse(error, addAlert, alertMessages.saveGroup) 

        }
        else{
          let errorMsg = error[0].message;
          if(errorMsg.includes("Duplicate contact cannot be added to Group")){ 
            errorResponse(error, addAlert,alertMessages.saveGroupContact);
          }  
          else if(errorMsg.includes("Please enter a unique Group Name.")){
            errorResponse(error, addAlert,errorMsg);
          }
          else { 
            errorResponse(error, addAlert, alertMessages.saveGroup) 
          }
        }
      }
    })();
  }
  
  /** Make an API call to get all areas */
  const getAllAreas = useCallback((request:any) => {
    (async () => {
      try {
        const areas: any = await getArea(request);
        setAreas(areas);
        setAreasCallComplete(true);
      } catch (error) {
        setAreasCallComplete(true);
        setAreaFailureCount(areaFailureCount + 1)
        errorResponse(error, addAlert, alertMessages.fieldSampleHomeData);
      }
    })();
  }, [setAreasCallComplete, setAreas, areaFailureCount, addAlert]);

  /** Make an API call to get all contacts */
  const getContacts = useCallback((request: any) => {
    setContactsCalled(true);

    (async () => {
      try {
        const contactPromise: any = await getContactsCall(request);
        setContactsCallComplete(true);
        setContacts(contactPromise?.contacts);
        setContactsCalled(false)

      } catch (error) {
        setContactsCallComplete(true);
        setContactFailureCount(contactFailureCount + 1)
        errorResponse(error, addAlert, alertMessages.fieldSampleHomeData);
        setContactsCalled(false)
      }
    })();
  }, [setContactsCallComplete, setContacts, contactFailureCount, setContactFailureCount, addAlert]);

  /** Make an API call to populate the initVals when editing a group */
  const getGroup = useCallback((request: any) => {
    (async () => {
      try {
        const group: any = await getGroupTable(request);
        const contacts: GroupContact[] = [];
        if(group?.groups && group.groups[0] && group.groups[0]?.groupContacts){
          for(const groupContact of group.groups[0].groupContacts) {
            const contact: GroupContact = {
              contactName: groupContact.name,
              contactPhone: groupContact.phoneNumber,
              contactExtension: groupContact.extension,
              title: groupContact.title,
              contactEmail: groupContact.email,
              priority: groupContact.priority,
              contactId: groupContact.contactId,
              groupContactId: groupContact.groupContactId
            }
            contacts.push(contact);
          }
         const sortedContacts = contacts? [...contacts].sort((a, b) => (a.priority && b.priority && a.priority > b.priority  ? 1 : -1)): contacts
          const editArea = {
            label: group?.groups[0]?.area.areaName,
            id: group?.groups[0]?.area.areaId
          }
          const groupEdit: FormVals = {
            groupId: group?.groups[0]?.groupId,
            groupName: group?.groups[0]?.groupName,
            area: editArea,
            contacts: sortedContacts,
            deleteContacts: []
          }
          setInitVals(groupEdit);
        }

      } catch (error) {
        errorResponse(error, addAlert,alertMessages.fieldSampleHomeData);
      }
    })();
  }, [addAlert]);

  /** Get Token */
  useEffect(() => {
    /** find access token and set to send to bearer */
    if((state.userTokens && token)|| localStorage.getItem('user')) {
      /** If the Token is refreshed this will set the @param token equal to updated value */
      setToken(state.userTokens.accessToken);
    }
  }, [state.userTokens, setToken, token]);

  /** Set Areas */
  useEffect(() => {
    if(areas.length <= 0 && areaFailureCount <= 2 && areasCallComplete) {
      setAreasCallComplete(false);
      const requestAreaReq = {
        token: token,
        setAreaCallComplete: setAreasCallComplete,
        setAreaFailureCount: setAreaFailureCount,
        areaFailureCount: areaFailureCount,
        addAlert: useAddAlert
      };
      getAllAreas(requestAreaReq);
    }
  }, [areas, getAllAreas, token, setAreaFailureCount, areasCallComplete, areaFailureCount]);

  /** Set Contacts */
  useEffect(() => {
    if(contacts.length <= 0 && !contactsCalled) {

      setContactsCallComplete(false);
      const requestContactReq = {
        token: token,
        setContactCallComplete: setContactsCallComplete,
        setContactFailureCount: setContactFailureCount,
        contactFailureCount: contactFailureCount,
        returnAll: true, 
        page: null,
        perPage: null,
        addAlert: useAddAlert
      };
      getContacts(requestContactReq);    
    }
  }, [contacts, getContacts, token, setContactFailureCount, contactsCallComplete, contactFailureCount, contactsCalled]);

    /** Determines the initial values of the fields if user clicks on edit group */
    useEffect(() => {
      if(editGroup){
        const requestGroupReq = {
          returnAll: false,
          groupId: editGroup.groupId,
          setCallComplete: setGroupCallComplete,
          setTableFail: setGroupFailureCount,
          addAlert: useAddAlert
        }
        getGroup(requestGroupReq);
      } else {
        let initVals = { 
          groupId: null,
          groupName: '', 
          area: null, 
          contacts:[{
            contactName: '',
            contctPhone: '',
            contactExtension: '',
            title: '',
            contactEmail: '',
            priority: 1,
            contactId: 0
          }], 
          deleteContacts: []
        }
        setInitVals(initVals)
      }
    }, [editGroup, getGroup]);

  return(
  <GridContainer  className="removePadding smooth-scrollbar" style={{height: '85%', maxHeight: '95%', overflowX: 'hidden'}}>
    <Grid row gap="md" className='page-heading'>
      <Grid
        desktop={{col:12}}
        className="display-flex flex-row flex-align-center flex-justify margin-bottom-205 paddingForModal">
          {
            editGroup ? (
              <span className="font-sans-lg text-bold">Edit Group</span>
            ):(
              <span className="font-sans-lg text-bold">Add a Group</span>
            )
          }
      </Grid>
    </Grid>
    <hr style={{width: '100%', marginBottom: 0}}></hr>

    <Formik
      initialValues={initVals}
      validateOnBlur={true}
      validateOnChange={true}
      validateOnMount={true}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={(values) => handleSubmit(values) }>
      {({
        values,
        errors,
        touched,
        handleSubmit,
        setFieldValue,
        handleBlur,
        isValid,
        setFieldTouched
      }) => { 
        const setGroupName = (event: SelectChangeEvent) => {
          setFieldTouched('groupName')
          if(event.target.value.length ===1 && /^\s/.test(event.target.value)){
            return
          }
          setFieldValue('groupName', event.target.value);
        };

        const setArea=(areaValue: any)=>{
          setFieldTouched('area');
          setFieldValue('area', areaValue);
        };

        const setContactName = (index: number, newValue: any) =>{

          if(values.contacts[index].groupContactId) {
           
            const deleteContacts = [...values.deleteContacts];
            deleteContacts.push(values.contacts[index]);
            setFieldValue('deleteContacts', deleteContacts);
          }
          setFieldTouched(`contacts[${index}]`);
          if(!newValue) {
            const contact = {
              contactName: '',
              contactPhone: '',
              contactExtension: '',
              title: '',
              contactEmail: '',
              priority: index,
              contactId: 0
            }
            setFieldValue(`contacts[${index}]`, contact);
          } 
          else {
            const updatedContact = {
              contactName: newValue.contactName,
              contactPhone: newValue.contactPhone,
              contactExtension: newValue.contactExtension,
              title: newValue.title,
              contactEmail: newValue.contactEmail,
              priority: index + 1,
              contactId: newValue.contactId
            }
            setFieldValue(`contacts[${index}]`, updatedContact);
          }
        };

        const addNewContact = (contactsLength: number) =>{
          const newContact = {
            contactName: '',
            contactPhone: '',
            contactExtension: '',
            title: '',
            contactEmail: '',
            priority: contactsLength + 1,
            id: contactsLength
          }
          setFieldValue('contacts', [...values.contacts, newContact])
        }
        const removeContact = (index: number) =>{
          if(values.contacts[index].groupContactId) {
            const deleteContacts = [...values.deleteContacts];
            deleteContacts.push(values.contacts[index]);
            setFieldValue('deleteContacts', deleteContacts);
          }
          const updateContacts = [...values.contacts];
          updateContacts.splice(index,1);
          setFieldValue('contacts', updateContacts);
        }

        return(
          <Form onSubmit={handleSubmit}>
            <div className="paddingForm">
              <Grid row desktop={{ col: 7 }}>
                <label id='createUpdateGroupName' htmlFor='createUpdateGroupName'>Group Name *</label>
                <TextField
                  value={values.groupName}
                  id='createUpdateGroupName'
                  onBlur={handleBlur}
                  error={!!errors.groupName && !!touched.groupName}
                  helperText={!!errors.groupName && !!touched.groupName ? errors.groupName : ''}
                  type='string'
                  size='medium'
                  fullWidth
                  onChange={(e: any) => setGroupName(e)}
                  placeholder='Input Name'
                />
              </Grid>
              <Grid row desktop={{ col: 7 }}>
                <label id='createUpdateGroupAreaName' htmlFor='createUpdateGroupAreaName'>Area Name *</label>
                <div className='searchAreas' style={{ width: '100%' }}>
                  <i className="fas fa-search" />
                  <Autocomplete
                    onBlur={handleBlur}
                    aria-labelledby='createUpdateGroupAreaName'
                    id='createUpdateGroupAreaName'
                    onChange={(event: any, newValue: any) => {
                      setArea(newValue);
                    }}
                    style={{ width: '100%' }}
                    value={values.area}
                    options={areas.map((area) => ({label: area.areaName, id: area.areaId}))}
                    renderInput={(params) => (
                      <TextField
                        placeholder='Search Areas'
                        style={{ width: '100%' }}
                        onBlur={handleBlur}
                        aria-label={'Search Area'}
                        data-testid={'search-area'}
                        error={!!errors.area && !!touched.area}
                        helperText={!!errors.area && !!touched.area ? errors.area : ''}
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          type: 'search',
                        }}
                      />
                    )}
                  />
                </div>
              </Grid>
              {
                values.contacts?.map((contact, index)=>
                  <div key={`${index}-${contact.contactName}`}>
                    {
                      index===0 && (
                        <>
                          <hr style={{width: '100%', marginBottom: '2rem', marginTop: '2rem'}}></hr>
                          <Grid row>
                            <span id='PrimaryContact' className='contactStyle'>Primary Contact</span>
                          </Grid>
                        </>

                      )
                    }
                    {
                      index > 0 && (
                        <Grid row style={{paddingBottom: '2%'}}>
                          <Grid desktop={{col: 4}}>
                            <span id={`additionalContact ${index}`} className='contactStyle'>Additional Contact ({index+1})</span>
                          </Grid>
                          <Button variant="text" onClick={()=>removeContact(index)} style={{textTransform: 'none', textDecoration: 'underline', color: '#707070'}}>
                            <CloseIcon fontSize='small'/>
                            Remove
                          </Button>
                      </Grid>
                      )
                    }
                    
                    <Grid row gap='sm'>
                      <Grid desktop={{ col: 5 }}>
                        <label id={`createUpdateGroupContactName-${contact.contactId}-${index}`} htmlFor={`createUpdateGroupContactName-${contact.contactId}-${index}`}>Contact Name *</label>
                        <div className='searchAreas' style={{ width: '100%' }}>
                          <i className="fas fa-search" />
                          <Autocomplete
                            onBlur={handleBlur}
                            onChange={(event: any, newValue: any) => {
                              setContactName(index, newValue);
                            }}
                            id={`createUpdateGroupContactName-${contact.contactId}-${index}`}
                            style={{ width: '100%' }}
                            value={values.contacts[index]}
                            options={contacts}
                            getOptionLabel={(option)=>option.contactName}
                            renderOption={(props, item) => (
                              <li {...props} key={item.contactId}>
                                <ListItemText>{item.contactName}</ListItemText>
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                placeholder='Search Names'
                                style={{ width: '100%' }}
                                onBlur={handleBlur}
                                error={!!errors.contacts && !!errors.contacts[index]
                                  && !!touched.contacts && !!touched.contacts[index]
                                }
                                helperText={!!errors.contacts && !!errors.contacts[index]
                                  && !!touched.contacts && !!touched.contacts[index] ? (errors.contacts[index]['contactName'] ? errors.contacts[index]['contactName']:errors.contacts[index]['contactId'] ) :''
                                }
                                {...params}
                                InputProps={{
                                  ...params.InputProps,
                                  type: 'search',
                                }}
                              />
                            )}
                          />
                        </div>
                        <div style={{marginTop: '-4%'}}>
                          <Button variant='text' onClick={() => history.push(`${ROUTE_PATHS.contacts}`)} style={{textTransform: 'none', textDecoration: 'underline', width: '75%'}}>Create New Contact<i className="fas fa-external-link-alt fa-2xs external-links" style={{paddingLeft: '2%', color:'#1a6ad3'}}/>
                          </Button>
                        </div>

                      </Grid>
                      <Grid desktop={{ col: 5 }} className="contactNumber">
                        <label id={`createUpdateGroupContactPhone-${contact.contactId}-${index}`} htmlFor={`createUpdateGroupContactPhone-${contact.contactId}-${index}`} className='disabled-label'>Government Phone</label>
                        <div className='contactPhone' style={{ width: '100%' }}>
                          <i className="fas fa-search" />
                          <TextField
                            className={'disabled-textfield'}                       
                            value={formatPhoneNumber(values.contacts[index].contactPhone)}
                            id={`createUpdateGroupContactPhone-${contact.contactId}-${index}`}
                            type='string'
                            onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                            size='medium'
                            fullWidth
                            placeholder='Government Phone'
                          />
                        </div>
                      </Grid>
                      <Grid desktop={{ col: 2 }} className="contactNumExt">
                        <label id={`createUpdateGroupContactExt-${contact.contactId}-${index}`} htmlFor={`createUpdateGroupContactExt-${contact.contactId}-${index}`} className='disabled-label'>Ext.</label>
                        <TextField
                          className={'disabled-textfield'}
                          value={values.contacts[index].contactExtension}
                          id={`createUpdateGroupContactExt-${contact.contactId}-${index}`}
                          type='string'
                          onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                          size='medium'
                          fullWidth
                          placeholder='Ext.'
                        />
                      </Grid>
                    </Grid>
                    
                    <Grid row gap='sm'>
                      <Grid desktop={{ col: 5 }}>
                        <label id={`createUpdateGroupContactEmail-${contact.contactId}-${index}`} htmlFor={`createUpdateGroupContactEmail-${contact.contactId}-${index}`} className='disabled-label'>Government Email</label>
                        <div className='contactPhone' style={{ width: '100%' }}>
                          <i className="fas fa-search" />
                          <TextField
                            className={'disabled-textfield'}
                            onBlur={handleBlur}
                            id={`createUpdateGroupContactEmail-${contact.contactId}-${index}`}
                            value={values.contacts[index].contactEmail}
                            type='string'
                            onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                            size='medium'
                            fullWidth
                            placeholder='Government Email'
                          />
                        </div>
                      </Grid>
                      <Grid desktop={{ col: 7 }} className="contactNumber">
                        <label id={`createUpdateGroupContactTitle-${contact.contactId}-${index}`} htmlFor={`createUpdateGroupContactTitle-${contact.contactId}-${index}`} className='disabled-label'>Position Title</label>
                        <div className='contactPhone' style={{ width: '100%' }}>

                          <i className="fas fa-search" />
                          <TextField
                            className={'disabled-textfield'}
                            value={values.contacts[index].title}
                            id={`createUpdateGroupContactTitle-${contact.contactId}-${index}`}
                            type='string'
                            onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                            size='medium'
                            fullWidth
                            placeholder='Position Title'
                          />
                        </div>
                      </Grid>
                    </Grid>
                    <hr style={{width: '100%'}}></hr>
                  </div>
                )
              }

            <Grid row style={{paddingBottom: '15%'}}>
              <Button variant="outlined" onClick={()=>addNewContact(values.contacts.length)}>
                <AddIcon fontSize='small'/>
                Add Contact
              </Button>
            </Grid>
            </div>

            <Grid row className='modalBottomDiv'>
              <Grid desktop={{col:10}} className="modalCancelDiv">
                <Button variant="outlined" style={{backgroundColor: 'white'}} onClick={()=>{setOpen(!open)}}>
                  Cancel
                </Button>
              </Grid> 
              {
                editGroup? (
                  <Grid desktop={{col:2}} className='modalSubmitDiv'>
                    <Button variant="contained" type="submit" aria-disabled={!isValid}>
                      Save
                    </Button>
                  </Grid>
                ) : (
                  <Grid desktop={{col:2}} className='modalSubmitDiv'>
                    <Button variant="contained" type="submit" aria-disabled={!isValid}>
                      Add
                    </Button>
                  </Grid>
                )
              }
            </Grid>
          </Form>
      )}}
    </Formik>
  </GridContainer>
  )
}